import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { Checkbox, Flex, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { routePaths } from 'router';
import type { TGetTransportsRes, TransportRes, TTransportSubtype } from 'api/swagger';
import { useRoleGuard, UserRole } from 'shared/hooks/use-role-guard';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { numberFormatter } from 'shared/utils/numbers/formatter.ts';
import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { formatShortDateTime } from 'shared/utils/time/format-short-date-time';
import { TransportSubTypes } from 'types/transport';
import { TransportUpdate } from '../transport-update';
import styles from './transport-list.module.scss';
import type { ITransportsTableProps } from './types';

export const TransportsList: React.FC<ITransportsTableProps> = (props) => {
  const { transports, isLoading } = props;
  const { t } = useTranslation();
  const tableData = useTransformToTableData(transports);
  const isActionColumnShown = useRoleGuard([UserRole.admin, UserRole.mechanic]);

  const columns: ColumnsType<TGetTransportsRes> = [
    {
      title: t('common:plate_number'),
      dataIndex: 'plateNumber',
      width: '18%',
      render: (plateNumber: string, { actualTrip, subcontractor }: TGetTransportsRes) => (
        <>
          <Flex align='start' gap={8}>
            <Typography.Text>{plateNumber}</Typography.Text>
            {actualTrip && (
              <Tag color='blue'>
                <Link
                  className={styles.actualTripLink}
                  to={generatePath(routePaths.trip, { tripId: String(actualTrip.id) })}>
                  {actualTrip.compositeId}
                </Link>
              </Tag>
            )}
          </Flex>
          {subcontractor && (
            <Flex align='start' className={styles.bottomContainer}>
              <Link to={generatePath(routePaths.client, { clientId: String(subcontractor?.id) })}>
                <UserOutlined /> {subcontractor?.shortName || subcontractor?.name}
              </Link>
            </Flex>
          )}
        </>
      ),
    },
    {
      title: t('transport:max_values'),
      dataIndex: 'values',
      render: (_: unknown, { maxLDM, maxVolume, maxWeight }: TransportRes) => {
        if (!maxLDM && !maxVolume && !maxWeight) {
          return null;
        }
        return (
          <Typography.Text>
            {maxLDM && numberFormatter(maxLDM)} LDM / {maxVolume && numberFormatter(maxVolume)} m³ /{' '}
            {maxWeight && numberFormatter(maxWeight)} kg
          </Typography.Text>
        );
      },
    },
    {
      title: '',
      dataIndex: 'subType',
      render: (subType: TTransportSubtype, { type }) => {
        return (
          <Typography.Text>
            <Tag>{type}</Tag>
            {TransportSubTypes[subType]}
          </Typography.Text>
        );
      },
    },
    {
      title: t('transport:geo'),
      dataIndex: 'values',
      render: (_: unknown, { lat, lng, geoUpdated, deviceKey }) => {
        if (!lat || !lng) return null;
        return (
          <div>
            {deviceKey && (
              <Tooltip title={deviceKey}>
                <EnvironmentOutlined style={{ marginRight: 8 }} />
              </Tooltip>
            )}
            {lat}, {lng}
            {geoUpdated && (
              <div
                style={{
                  fontSize: 'small',
                  color: 'gray',
                }}>
                {formatShortDateTime(geoUpdated).toLocaleString()}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('transport:driver'),
      dataIndex: 'driverFullName',
      render: (driverFullName: string) => {
        return <Typography.Text>{driverFullName}</Typography.Text>;
      },
    },
    {
      title: t('transport:needs_service'),
      dataIndex: 'needService',
      render: (needService: boolean) => <Checkbox checked={needService} className={styles.serviceStatusCheckbox} />,
    },
    {
      title: t('transport:in_service'),
      dataIndex: 'inService',
      render: (inService: boolean) => <Checkbox checked={inService} className={styles.serviceStatusCheckbox} />,
    },
    {
      title: t('transport:not_used'),
      dataIndex: 'notUsed',
      render: (notUsed: boolean) => <Checkbox checked={notUsed} className={styles.serviceStatusCheckbox} />,
    },
    {
      title: t('transport:last_service_update'),
      dataIndex: 'lastServiceUpdate',
      render: (lastServiceUpdate: string) => (lastServiceUpdate ? formatDateTime(lastServiceUpdate) : null),
    },
    ...(isActionColumnShown
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: unknown, record: TGetTransportsRes) => <TransportUpdate transport={record} />,
          },
        ]
      : []),
  ];

  return (
    <div>
      <Table
        rowClassName={(record) => (record.notUsed ? styles.canceledRow : '')}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </div>
  );
};
